<script setup>
import { reactive, ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { uploadFile } from 'dashboard/helper/uploadHelper';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();
const route = useRoute();

const accountId = computed(() => route.params.accountId);

const allWhatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const labels = useMapGetter('labels/getLabels');
const uiFlags = useMapGetter('campaigns/getUIFlags');
const isCreating = computed(() => uiFlags.value.isCreating);

const baileysinboxOptions = computed(() =>
  (allWhatsAppInboxes.value || [])
    .filter(i => i.provider === 'baileys')
    .map(i => ({ value: i.id, label: i.name }))
);

const audienceList = computed(() =>
  (labels.value || []).map(l => ({ value: l.id, label: l.title }))
);

const initialState = {
  title: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
};

const state = reactive({ ...initialState });

const blocks = ref([{ type: 'text', body: '' }]);

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
};
const v$ = useVuelidate(rules, state);

const currentDateTime = computed(() => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
});

const getError = (field, key) =>
  v$.value[field].$error ? t(`BAILEYS_CAMPAIGN.CREATE.FORM.${key}.ERROR`) : '';

const formErrors = computed(() => ({
  title: getError('title', 'TITLE'),
  inbox: getError('inboxId', 'INBOX'),
  scheduledAt: getError('scheduledAt', 'SCHEDULED_AT'),
  audience: getError('selectedAudience', 'AUDIENCE'),
}));

const hasValidBlocks = computed(
  () =>
    blocks.value.length > 0 &&
    blocks.value.every(b => {
      if (b.type === 'text') return b.body?.trim().length > 0;
      if (b.type === 'media') return b.blob_key != null;
      return false;
    })
);

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasValidBlocks.value || isCreating.value
);

const MEDIA_TYPES = [
  { key: 'image', icon: 'i-lucide-image', labelKey: 'ADD_IMAGE', accept: 'image/*' },
  { key: 'video', icon: 'i-lucide-video', labelKey: 'ADD_VIDEO', accept: 'video/*' },
  { key: 'audio', icon: 'i-lucide-music', labelKey: 'ADD_AUDIO', accept: 'audio/*' },
  { key: 'document', icon: 'i-lucide-file', labelKey: 'ADD_DOCUMENT', accept: '*/*' },
];

const MEDIA_ICONS = {
  image: 'i-lucide-image',
  video: 'i-lucide-video',
  audio: 'i-lucide-music',
  document: 'i-lucide-file',
};

const addTextBlock = () =>
  blocks.value.push({ type: 'text', body: '' });

const addMediaBlock = fileType =>
  blocks.value.push({
    type: 'media',
    file_type: fileType,
    blob_key: null,
    file_url: null,
    filename: null,
    content_type: null,
    caption: '',
    uploading: false,
  });

const removeBlock = idx => blocks.value.splice(idx, 1);

const moveBlock = (idx, direction) => {
  const target = idx + direction;
  if (target < 0 || target >= blocks.value.length) return;
  const tmp = blocks.value[idx];
  blocks.value[idx] = blocks.value[target];
  blocks.value[target] = tmp;
};

const handleFileUpload = async (block, event) => {
  const file = event.target.files[0];
  if (!file) return;
  block.uploading = true;
  try {
    const result = await uploadFile(file, accountId.value);
    block.blob_key = result.blobKey;
    block.file_url = result.fileUrl;
    block.filename = file.name;
    block.content_type = file.type;
  } catch (e) {
    // user can retry by re-selecting
  } finally {
    block.uploading = false;
  }
};

const applyFormat = (blockIdx, wrap) => {
  const textarea = document.getElementById(`baileys-block-${blockIdx}`);
  if (!textarea) return;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const current = blocks.value[blockIdx].body || '';
  const selected = current.substring(start, end);

  const isAlreadyWrapped =
    selected.startsWith(wrap) &&
    selected.endsWith(wrap) &&
    selected.length > wrap.length * 2;

  if (isAlreadyWrapped) {
    blocks.value[blockIdx].body =
      current.substring(0, start) +
      selected.slice(wrap.length, -wrap.length) +
      current.substring(end);
  } else {
    blocks.value[blockIdx].body =
      current.substring(0, start) + wrap + selected + wrap + current.substring(end);
  }

  nextTick(() => {
    textarea.focus();
    const newPos = isAlreadyWrapped ? end - wrap.length : end + wrap.length;
    textarea.setSelectionRange(start + wrap.length, newPos);
  });
};

const formatToUTCString = dt => (dt ? new Date(dt).toISOString() : null);

const resetState = () => {
  Object.assign(state, initialState);
  blocks.value = [{ type: 'text', body: '' }];
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid || !hasValidBlocks.value) return;

  const firstText = blocks.value.find(b => b.type === 'text');
  const messageSummary = firstText?.body?.trim() || state.title;

  emit('submit', {
    title: state.title,
    message: messageSummary,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience.map(id => ({ id, type: 'Label' })),
    template_params: {
      baileys_messages: blocks.value.map(b => ({ ...b, uploading: undefined })),
    },
  });

  resetState();
  handleCancel();
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Title -->
    <Input
      v-model="state.title"
      :label="t('BAILEYS_CAMPAIGN.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('BAILEYS_CAMPAIGN.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <!-- Inbox -->
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('BAILEYS_CAMPAIGN.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        v-model="state.inboxId"
        :options="baileysinboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('BAILEYS_CAMPAIGN.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- Audience -->
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('BAILEYS_CAMPAIGN.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('BAILEYS_CAMPAIGN.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('BAILEYS_CAMPAIGN.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Scheduled At -->
    <Input
      v-model="state.scheduledAt"
      :label="t('BAILEYS_CAMPAIGN.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('BAILEYS_CAMPAIGN.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Message Builder -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.LABEL') }}
      </label>

      <p class="text-xs text-n-slate-11 -mt-1">
        {{ t('BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.FORMAT_HINT') }}
      </p>

      <!-- Blocks list -->
      <div class="flex flex-col gap-2">
        <div
          v-for="(block, idx) in blocks"
          :key="idx"
          class="border border-n-weak rounded-lg p-3 bg-n-alpha-1 flex flex-col gap-2"
        >
          <!-- Block header: type label + move/remove controls -->
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11">
              <span
                :class="block.type === 'text' ? 'i-lucide-type' : (MEDIA_ICONS[block.file_type] || 'i-lucide-paperclip')"
                class="w-3.5 h-3.5"
              />
              <span>{{ block.type === 'text' ? t('BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.ADD_TEXT') : block.file_type }}</span>
            </div>
            <div class="flex items-center gap-1">
              <button
                type="button"
                :disabled="idx === 0"
                class="p-1 rounded text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-30 disabled:cursor-not-allowed"
                @click="moveBlock(idx, -1)"
              >
                <span class="i-lucide-arrow-up w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                :disabled="idx === blocks.length - 1"
                class="p-1 rounded text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-30 disabled:cursor-not-allowed"
                @click="moveBlock(idx, 1)"
              >
                <span class="i-lucide-arrow-down w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                class="p-1 rounded text-n-slate-10 hover:text-ruby-800 hover:bg-n-alpha-2"
                @click="removeBlock(idx)"
              >
                <span class="i-lucide-trash-2 w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <!-- Text block -->
          <template v-if="block.type === 'text'">
            <div class="flex items-center gap-1 border-b border-n-weak pb-2">
              <button
                type="button"
                class="px-2 py-0.5 text-xs font-bold rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
                title="Negrito: *texto*"
                @click="applyFormat(idx, '*')"
              >
                B
              </button>
              <button
                type="button"
                class="px-2 py-0.5 text-xs italic rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
                title="Itálico: _texto_"
                @click="applyFormat(idx, '_')"
              >
                I
              </button>
              <button
                type="button"
                class="px-2 py-0.5 text-xs line-through rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
                title="Riscado: ~texto~"
                @click="applyFormat(idx, '~')"
              >
                S
              </button>
              <button
                type="button"
                class="px-2 py-0.5 text-xs font-mono rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
                title="Monoespaçado: ```texto```"
                @click="applyFormat(idx, '```')"
              >
                &lt;/&gt;
              </button>
            </div>
            <textarea
              :id="`baileys-block-${idx}`"
              v-model="block.body"
              rows="3"
              :placeholder="t('BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.TEXT_PLACEHOLDER')"
              class="w-full resize-none rounded bg-n-alpha-black2 border border-n-weak px-3 py-2 text-sm text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:border-woot-500 dark:focus:border-woot-500"
            />
          </template>

          <!-- Media block -->
          <template v-else-if="block.type === 'media'">
            <div class="flex items-center gap-2">
              <label
                class="cursor-pointer flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-n-weak hover:bg-n-alpha-2 text-n-slate-11"
              >
                <span v-if="!block.uploading" class="i-lucide-upload w-3.5 h-3.5" />
                <span v-else class="i-lucide-loader-2 w-3.5 h-3.5 animate-spin" />
                {{ block.uploading
                  ? t('BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.UPLOADING')
                  : t('BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.UPLOAD') }}
                <input
                  type="file"
                  class="hidden"
                  :accept="MEDIA_TYPES.find(m => m.key === block.file_type)?.accept || '*/*'"
                  :disabled="block.uploading"
                  @change="handleFileUpload(block, $event)"
                />
              </label>
              <span
                v-if="block.filename"
                class="text-xs text-n-slate-11 truncate max-w-[160px]"
                :title="block.filename"
              >
                <span class="i-lucide-check w-3 h-3 text-green-600 inline mr-0.5" />
                {{ block.filename }}
              </span>
              <span v-else-if="!block.uploading" class="text-xs text-n-slate-9">
                {{ t('BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.NO_FILE') }}
              </span>
            </div>
            <input
              v-model="block.caption"
              type="text"
              :placeholder="t('BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.CAPTION_PLACEHOLDER')"
              class="w-full rounded bg-n-alpha-black2 border border-n-weak px-3 py-1.5 text-sm text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:border-woot-500 dark:focus:border-woot-500"
            />
          </template>
        </div>
      </div>

      <p
        v-if="blocks.length === 0"
        class="text-xs text-ruby-800"
      >
        {{ t('BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.EMPTY') }}
      </p>

      <!-- Add block buttons -->
      <div class="flex flex-wrap gap-1.5 pt-1">
        <button
          type="button"
          class="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border border-dashed border-n-weak hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
          @click="addTextBlock"
        >
          <span class="i-lucide-type w-3.5 h-3.5" />
          {{ t('BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.ADD_TEXT') }}
        </button>
        <button
          v-for="media in MEDIA_TYPES"
          :key="media.key"
          type="button"
          class="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border border-dashed border-n-weak hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
          @click="addMediaBlock(media.key)"
        >
          <span :class="media.icon" class="w-3.5 h-3.5" />
          {{ t(`BAILEYS_CAMPAIGN.CREATE.FORM.MESSAGES.${media.labelKey}`) }}
        </button>
      </div>
    </div>

    <!-- Action buttons -->
    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('BAILEYS_CAMPAIGN.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('BAILEYS_CAMPAIGN.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isSubmitDisabled"
      />
    </div>
  </form>
</template>
